// The guard on every /session/:sessionCode route (#403).
//
// A Session URL only means something to a phone that is actually in that
// Session: the Session Code lives in the store, and the store is per-tab
// sessionStorage. A Session URL copied out of the address bar and forwarded,
// cleared storage or a new browser therefore lands on a Session route with
// nothing behind it — an empty waiting room with a dead Start button, or an
// empty Deck. (The Invite Link itself is /join?code=…, so it never reaches
// this guard.) Join already handles the prefilled code, the name entry and
// an expired link, so send them there instead.
//
// The comparison is against the URL's code, not just "is there any Session":
// that also covers a link for a different Session opened in a tab already in
// one, and it makes a rejected rejoin redirect for free — socketBindings
// resets the store and toasts the server's reason, and this re-renders.

import { Link, Navigate, Outlet, useLocation, useParams } from 'react-router';
import { useSessionStore } from '../stores/sessionStore';

export default function RequireSession() {
  const { sessionCode } = useParams<{ sessionCode: string }>();
  const { pathname } = useLocation();
  const {
    sessionCode: storedSessionCode,
    sessionStatus,
    lobby,
    participants,
    currentUserId,
    isConnected,
    rejectedSessionCode,
  } = useSessionStore();

  if (storedSessionCode !== sessionCode) {
    return (
      <Navigate
        to={`/join?code=${encodeURIComponent(sessionCode ?? '')}${rejectedSessionCode === sessionCode ? '&resume=failed' : ''}`}
        replace
      />
    );
  }

  if (!isConnected && sessionStatus !== 'expired')
    return (
      <main
        className="min-h-screen p-6 space-y-4"
        style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top))' }}
      >
        <h1 className="text-2xl">Reconnecting to your session</h1>
        {/* Also shown to an online phone whose rejoin failed, so only an offline
            one hears about the connection (#511). Home keeps the Session: Home
            offers "Return to session". */}
        <p role="status">
          {navigator.onLine ? 'Couldn’t reach your session yet.' : 'You’re offline.'} Your place is
          saved.
        </p>
        <div className="flex gap-3">
          <button
            className="btn btn-primary"
            onClick={() => {
              void import('../services/socketBindings').then(({ reconcileSession }) =>
                reconcileSession()
              );
            }}
          >
            Try again
          </button>
          <Link to="/" className="btn btn-secondary inline-flex items-center">
            Home
          </Link>
        </div>
      </main>
    );

  const lobbyPath = `/session/${sessionCode}`;
  const waitingParticipant = participants.find(
    (participant) => participant.participantId === currentUserId
  )?.waitingForNextRound;
  if (pathname !== lobbyPath && (waitingParticipant || (lobby && sessionStatus === 'waiting'))) {
    return <Navigate to={lobbyPath} replace />;
  }

  return <Outlet />;
}
